import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';

interface Member {
  id: string;
  opportunityId: string;
  contactId: string;
  contactName: string;
  contactJobTitle: string | null;
  role: string;
  influenceLevel: string;
  notes: string | null;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
}

/**
 * Story 8.3 — influence map UI, hung off opportunity detail via
 * `/opportunities/:id/influence`. v1 ships add-member with role +
 * level dropdowns; editing and deletion are minimal inline actions.
 */
export function InfluenceMapPage() {
  const { id } = useParams<{ id: string }>();
  const [members, setMembers] = useState<Member[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [form, setForm] = useState({
    contactId: '',
    role: 'CHAMPION',
    influenceLevel: 'HIGH',
  });

  const refresh = useCallback(async () => {
    if (!id) return;
    const { data } = await apiClient.get<Member[]>('/influence-map', {
      params: { opportunityId: id },
    });
    setMembers(data);
  }, [id]);

  useEffect(() => {
    refresh().catch(() => setMembers([]));
  }, [refresh]);

  useEffect(() => {
    apiClient
      .get<Contact[]>('/contacts', { params: { accountId: '' } })
      .then(({ data }) => setContacts(data))
      .catch(() => setContacts([]));
  }, []);

  async function addMember() {
    if (!id || !form.contactId) return;
    await apiClient.post('/influence-map', {
      opportunityId: id,
      contactId: form.contactId,
      role: form.role,
      influenceLevel: form.influenceLevel,
    });
    await refresh();
  }

  async function remove(memberId: string) {
    await apiClient.delete(`/influence-map/${memberId}`);
    await refresh();
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Mapa de influencia</h1>
      <div className="rounded-lg border bg-card p-4">
        <div className="flex flex-wrap gap-2">
          <select
            className="rounded-md border bg-background px-2 py-1 text-sm"
            value={form.contactId}
            onChange={(e) => setForm({ ...form, contactId: e.target.value })}
          >
            <option value="">— contacto —</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.firstName} {c.lastName}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border bg-background px-2 py-1 text-sm"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          >
            {[
              'DECISION_MAKER',
              'ECONOMIC_BUYER',
              'TECHNICAL_EVALUATOR',
              'CHAMPION',
              'BLOCKER',
              'LEGAL_PROCUREMENT',
            ].map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
          <select
            className="rounded-md border bg-background px-2 py-1 text-sm"
            value={form.influenceLevel}
            onChange={(e) => setForm({ ...form, influenceLevel: e.target.value })}
          >
            {['HIGH', 'MEDIUM', 'LOW'].map((l) => (
              <option key={l}>{l}</option>
            ))}
          </select>
          <Button size="sm" onClick={addMember}>
            Añadir
          </Button>
        </div>
      </div>
      <ul className="divide-y rounded-lg border bg-card">
        {members.map((m) => (
          <li key={m.id} className="flex items-center justify-between px-4 py-3">
            <div>
              <div className="font-medium">{m.contactName || m.contactId.slice(0, 8)}</div>
              <div className="text-xs text-muted-foreground">
                {m.role} · {m.influenceLevel}
                {m.contactJobTitle ? ` · ${m.contactJobTitle}` : ''}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => remove(m.id)}>
              Quitar
            </Button>
          </li>
        ))}
        {members.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-muted-foreground">—</li>
        )}
      </ul>
    </div>
  );
}
